import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useGroups } from '../hooks/useGroups';
import { useDebounce } from '../hooks/useDebounce';
import { usePagination } from '../hooks/usePagination';
import { applyGroupFilters, getGroupPrice } from '../utils/groupFilters';
import { getUniqueGroupTopics } from '../utils/topicClassifier';
import { GroupRow, formatMemberCount, formatPrice } from '../components/GroupRow';
import { SearchBar } from '../components/SearchBar';
import { Pagination } from '../components/Pagination';
import { ResultCount } from '../components/ResultCount';
import { ErrorMessage } from '../components/ErrorMessage';
import { SkeletonList } from '../components/SkeletonList';
import type { GroupFilterState } from '../../shared/types';

/**
 * GroupPage — displays groups in a table (desktop) or card list (mobile).
 * Requirements: 5.1–5.7, 7.1–7.8, 8.1–8.7, 11.5, 12.2
 */
const GroupPage: React.FC = () => {
  const isDesktop = useMediaQuery('(min-width:768px)'); // ≥768px per Req 11.5

  const { data, isLoading, error, mutate } = useGroups();

  const [searchQuery, setSearchQuery] = useState('');
  const [minMembers, setMinMembers] = useState<string>('');
  const [maxMembers, setMaxMembers] = useState<string>('');
  const [topicFilter, setTopicFilter] = useState('Tất cả');
  const [rangeError, setRangeError] = useState('');
  const [bannerOpen, setBannerOpen] = useState(true);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const minVal = minMembers === '' ? null : Number(minMembers);
  const maxVal = maxMembers === '' ? null : Number(maxMembers);

  const filterState: GroupFilterState = {
    searchQuery: debouncedSearch,
    minMembers: rangeError ? null : minVal,
    maxMembers: rangeError ? null : maxVal,
    topicFilter,
    currentPage: 1,
  };

  const allGroups = data ?? [];
  const topics = getUniqueGroupTopics(allGroups);
  const filtered = applyGroupFilters(allGroups, filterState);

  const { currentPage, totalPages, setPage, resetPage, paginatedItems } = usePagination(filtered.length);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    resetPage();
  }, [resetPage]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMinMembers(val);
    const min = val === '' ? null : Number(val);
    const max = maxMembers === '' ? null : Number(maxMembers);
    if (min !== null && max !== null && min > max) {
      setRangeError('Giá trị tối thiểu không được lớn hơn tối đa');
    } else {
      setRangeError('');
    }
    resetPage();
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMaxMembers(val);
    const min = minMembers === '' ? null : Number(minMembers);
    const max = val === '' ? null : Number(val);
    if (min !== null && max !== null && min > max) {
      setRangeError('Giá trị tối thiểu không được lớn hơn tối đa');
    } else {
      setRangeError('');
    }
    resetPage();
  };

  const handleTopicClick = (topic: string) => {
    setTopicFilter(topic);
    resetPage();
  };

  const paginated = paginatedItems(filtered);

  return (
    <Box component="main" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Group
      </Typography>

      {/* Warranty / info banner */}
      <Collapse in={bannerOpen}>
        <Alert
          severity="info"
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%', overflow: 'hidden' } }}
          action={
            <Button color="inherit" size="small" onClick={() => setBannerOpen(false)} sx={{ whiteSpace: 'nowrap' }}>
              Ẩn
            </Button>
          }
        >
          <strong>⚠️ Lưu ý:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20, wordBreak: 'break-word' }}>
            <li>Bảo hành back group, hỗ trợ đổi tên</li>
            <li>Giá ước tính = số thành viên × 150đ/người</li>
            <li>Liên hệ: <a href="https://www.facebook.com/premium69gr" target="_blank" rel="noopener noreferrer">facebook.com/premium69gr</a></li>
          </ul>
        </Alert>
      </Collapse>

      {/* Search + Filter */}
      <section aria-label="Tìm kiếm và lọc nhóm">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <SearchBar
              onSearch={handleSearch}
              ariaLabel="Tìm kiếm nhóm"
              placeholder="Tìm theo tên nhóm..."
            />
          </Box>
        </Box>

        {/* Advanced filters — open by default */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={true}
          sx={{ border: '1px solid', borderColor: 'divider', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<span>▼</span>} aria-controls="group-filter-content">
            <Typography variant="body2" color="text.secondary">Bộ lọc nâng cao</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Topic chips */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Chủ đề
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    gap: 1,
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'divider' },
                  }}
                >
                  {['Tất cả', ...topics].map(topic => (
                    <Chip
                      key={topic}
                      label={topic}
                      size="small"
                      onClick={() => handleTopicClick(topic)}
                      color={topicFilter === topic ? 'primary' : 'default'}
                      variant={topicFilter === topic ? 'filled' : 'outlined'}
                      sx={{ flexShrink: 0, cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Member range */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="Thành viên tối thiểu (K)"
                  type="number"
                  size="small"
                  value={minMembers}
                  onChange={handleMinChange}
                  inputProps={{ min: 0, max: 1000, 'aria-label': 'Thành viên tối thiểu tính theo nghìn' }}
                  error={!!rangeError}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="Thành viên tối đa (K)"
                  type="number"
                  size="small"
                  value={maxMembers}
                  onChange={handleMaxChange}
                  inputProps={{ min: 0, max: 1000, 'aria-label': 'Thành viên tối đa tính theo nghìn' }}
                  error={!!rangeError}
                  sx={{ width: 180 }}
                />
              </Box>
              {rangeError && (
                <Typography color="error" variant="body2">
                  {rangeError}
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </section>

      {/* Loading */}
      {isLoading && <SkeletonList count={10} />}

      {/* Error */}
      {error && !isLoading && (
        <ErrorMessage
          message="Không thể tải dữ liệu nhóm."
          onRetry={() => mutate()}
        />
      )}

      {/* Results */}
      {!isLoading && !error && (
        <section aria-label="Danh sách nhóm">
          <ResultCount count={filtered.length} />

          {filtered.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Không tìm thấy nhóm nào phù hợp
            </Typography>
          ) : isDesktop ? (
            /* Desktop: Table */
            <TableContainer component={Paper}>
              <Table aria-label="Bảng danh sách nhóm">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Tên nhóm</strong></TableCell>
                    <TableCell align="right"><strong>Số thành viên</strong></TableCell>
                    <TableCell align="right"><strong>Giá ước tính</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((group, idx) => (
                    <TableRow key={group.link || group.name + idx} hover>
                      <TableCell>
                        {group.link ? (
                          <Link href={group.link} target="_blank" rel="noopener noreferrer" underline="hover" aria-label={`${group.name} (mở trong tab mới)`}>
                            {group.name}
                          </Link>
                        ) : (
                          group.name
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatMemberCount(group.member_count)}
                      </TableCell>
                      <TableCell align="right">
                        ~{formatPrice(getGroupPrice(group))} đ
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Mobile: Card list */
            <Box>
              {paginated.map((group, idx) => (
                <GroupRow key={group.link || group.name + idx} group={group} />
              ))}
            </Box>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            onPageChange={setPage}
          />
        </section>
      )}
    </Box>
  );
};

export default GroupPage;
